import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";

import dayjs from "dayjs";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { axiosInstance, useClientInfo } from "../utils/commonImports";

const MISCustomerVehicle = () => {
  const { clientId } = useClientInfo();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [reportType, setReportType] = useState("customer-vehicle-group");

  const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
  const [toDate, setToDate] = useState(dayjs());

  const fetchData = async () => {
    if (!clientId) return;

    const formattedFrom = fromDate.format("YYYY-MM-DD");
    const formattedTo = toDate.format("YYYY-MM-DD");

    setLoading(true);

    try {
      const res = await axiosInstance.get(`/api/mis/${reportType}`, {
        params: {
          clientId,
          fromDate: formattedFrom,
          toDate: formattedTo,
        },
      });

      setRows(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Dynamic column key based on report type
  const dynamicKey = useMemo(() => {
    if (reportType === "customer-vehicle-group") return "vehicle_group";
    if (reportType === "customer-vehicle") return "vehiclename";
    if (reportType === "customer-vendor") return "vendorname";
    return "";
  }, [reportType]);

  // 🔹 Extract dynamic columns
  const columns = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      set.add(r[dynamicKey] || "OTHERS");
    });
    return Array.from(set).sort();
  }, [rows, dynamicKey]);

  // 🔹 Pivot
  const pivotData = useMemo(() => {
    const grouped = {};

    rows.forEach((row) => {
      const key = row[dynamicKey] || "OTHERS";

      if (!grouped[row.customername]) {
        grouped[row.customername] = {};
      }

      grouped[row.customername][key] = Number(row.total_amount);
    });

    return grouped;
  }, [rows, dynamicKey]);

  // 🔹 Column totals
  const columnTotals = useMemo(() => {
    const totals = {};

    columns.forEach((col) => {
      totals[col] = rows
        .filter((r) => (r[dynamicKey] || "OTHERS") === col)
        .reduce((sum, r) => sum + Number(r.total_amount), 0);
    });

    return totals;
  }, [rows, columns, dynamicKey]);

  const getRowTotal = (values) => {
    return columns.reduce((sum, col) => sum + Number(values[col] || 0), 0);
  };

  const grandTotal = Object.values(columnTotals).reduce(
    (sum, val) => sum + val,
    0,
  );

  return (
    <Box sx={{ p: 2 }}>
      {/* 🔹 FILTER SECTION */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Report Type</InputLabel>
            <Select
              value={reportType}
              label="Report Type"
              onChange={(e) => {
                setReportType(e.target.value);
                setRows([]); // clear old data
              }}
            >
              <MenuItem value="customer-vehicle-group">
                Customer Vs Vehicle Group
              </MenuItem>
              <MenuItem value="customer-vehicle">Customer Vs Vehicle</MenuItem>
              <MenuItem value="customer-vendor">Customer Vs Vendor</MenuItem>
            </Select>
          </FormControl>

          <Grid item xs={12} sm={2.2}>
            <DatePicker
              label="From Date"
              value={fromDate}
              format="DD/MM/YYYY"
              onChange={(newValue) => setFromDate(newValue)}
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2.2}>
            <DatePicker
              label="To Date"
              value={toDate}
              format="DD/MM/YYYY"
              onChange={(newValue) => setToDate(newValue)}
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
            />
          </Grid>
          <Button variant="contained" onClick={fetchData}>
            Search
          </Button>
        </Box>
      </LocalizationProvider>

      {/* 🔹 HEADER */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          MIS REPORT
        </Typography>

        <Typography sx={{ fontSize: "0.8rem" }}>
          {reportType.replaceAll("-", " ").toUpperCase()}
        </Typography>

        <Typography sx={{ fontSize: "0.75rem" }}>
          {fromDate?.format("DD/MM/YYYY")} to {toDate?.format("DD/MM/YYYY")}
        </Typography>
      </Box>

      {/* 🔹 TABLE */}
     <TableContainer
  component={Paper}
  sx={{
    overflowX: "auto",
    border: "1px solid #bbb",
  }}
>
  <Table
    size="small"
    sx={{
      minWidth: columns.length * 120, // auto grow based on columns
      borderCollapse: "collapse",
      "& th, & td": {
        border: "1px solid #bbb",
        fontSize: "0.70rem",
        padding: "4px 6px",
      },
    }}
  >
    <TableHead>
      <TableRow sx={{ backgroundColor: "#f3f4f6" }}>
        <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>
          Customer
        </TableCell>

        {columns.map((col) => (
          <TableCell
            key={col}
            align="right"
            sx={{ fontWeight: 700 }}
          >
            {col}
          </TableCell>
        ))}

        <TableCell align="right" sx={{ fontWeight: 700 }}>
          Total
        </TableCell>
      </TableRow>
    </TableHead>

    <TableBody>
      {loading ? (
        <TableRow>
          <TableCell colSpan={columns.length + 2} align="center">
            <CircularProgress size={20} />
          </TableCell>
        </TableRow>
      ) : rows.length === 0 ? (
        <TableRow>
          <TableCell colSpan={columns.length + 2} align="center">
            No Data Found
          </TableCell>
        </TableRow>
      ) : (
        Object.entries(pivotData).map(([customer, values]) => (
          <TableRow key={customer}>
            <TableCell>{customer}</TableCell>

            {columns.map((col) => (
              <TableCell key={col} align="right">
                {values[col] || 0}
              </TableCell>
            ))}

            <TableCell align="right" sx={{ fontWeight: 700 }}>
              {getRowTotal(values)}
            </TableCell>
          </TableRow>
        ))
      )}
    </TableBody>

    <TableFooter>
      <TableRow sx={{ backgroundColor: "#e5e7eb" }}>
        <TableCell sx={{ fontWeight: 700 }}>
          Grand Total
        </TableCell>

        {columns.map((col) => (
          <TableCell key={col} align="right" sx={{ fontWeight: 700 }}>
            {columnTotals[col] || 0}
          </TableCell>
        ))}

        <TableCell align="right" sx={{ fontWeight: 700 }}>
          {grandTotal}
        </TableCell>
      </TableRow>
    </TableFooter>
  </Table>
</TableContainer>

    </Box>
  );
};

export default MISCustomerVehicle;
